/**
 * 篮球的动作适配（纯函数）：规则事件怎么交给播放器、球在爪里画在哪、段的截断门槛。播放器本身不认识篮球，这些口径都在这里。
 * - 切段：照 B 的映射（basketClipForEvent，F0 修订 B3）。起点状态对不上的由播放器挂起（B7）；回球、终局撞上正在播的 react 段，等到它的可切换帧（帧 4）再切（C2）。
 * - 终局（ended，时间到），照 F0 修订 G1「找路不得穿过代表规则事件的段」：
 *   等球（WAIT）时播 end（照 C2 等帧 4）；还在空中，end 挂起，等规则的 landed 播完 land、到 WAIT 再进（B7）；
 *   持球、蓄力时没有不经规则段的路（经 shoot、land 等于演一个不存在的投篮），直接落到 END 的稳定姿势（end 末帧），发 fallback{no_path}。
 * - 结束时还在空中（F0 修订 G2）：规则核心先补发 landed（B 主修）；前端兜底——ended 之后一次起跳的时长（JUMP_MS）内 landed 没来，
 *   就直接落到 END 的稳定姿势、发 fallback{no_path}：不演落地，也不卡在滞空（settleAfterEnded）。
 * - END 画不出来（清单里的 end 段不合格被去掉，F0 修订 G3）：不落过去，不切 end 也不挂起，发 fallback{no_target_pose}。
 *   站在着地的状态（HOLD、CHARGE、WAIT）上就不动，接着播这个状态的循环（不定格）；过渡途中或在空中，退到 WAIT，没有再退到 HOLD，播它的待机循环。
 * - 球在爪里：动作帧有附着点画附着点（B4）；这一段的附着点还没出来（接球段帧 0–2），从球落下的地方往第一个附着点挪；都没有就交回 null（画规则的持球点）。
 */
import { createPlayer, currentFrame, cut, settle, standAt, type ClipLimit, type MotionEvent, type MotionPlayer, type PreparedMotion } from "@/features/pet_motion";
import { JUMP_MS, basketClipForEvent, type BasketEvent, type BasketStance } from "../core";
import { holdPointOnCourt, type BasketPlacement } from "./basketDraw";
import { ruleEventIdOf } from "./SnakeGame";

/**
 * 段的时长门槛（F0 修订 B5，B 给的实测数）：
 * - shoot 的滞空姿势（末帧）要在起跳 450 毫秒以内露出来；
 * - react_score、react_miss 整段 ≤1000 毫秒（判定到回球最短约 1017／1050 毫秒，B 请 ≤1000）。
 */
export const BASKET_CLIP_LIMITS: Readonly<Record<string, ClipLimit>> = {
  shoot: { limitMs: JUMP_MS, need: "last_frame" },
  react_score: { limitMs: 1000, need: "whole" },
  react_miss: { limitMs: 1000, need: "whole" },
};

/** 开局：持球站好，只有朝右一套（B1） */
export function startBasketPlayer(motion: PreparedMotion): MotionPlayer {
  return createPlayer(motion, "HOLD", "right");
}

/** 一条规则事件交给播放器；stanceAfter 是这批事件推进完以后规则核心里 TA 的姿态（终局时用） */
export function applyBasketEvent(player: MotionPlayer, event: BasketEvent, stanceAfter: BasketStance): { player: MotionPlayer; events: MotionEvent[] } {
  const clip = basketClipForEvent(event);
  if (!clip) return { player, events: [] };
  if (event.type === "ended") {
    // END 画不出来：不落过去（G3）
    if (!player.motion.stable.has("END")) return holdOrRetreat(player, "END");
    // 持球、蓄力：没有不经规则段的路，直接落到 END（G1）
    if (stanceAfter === "holding" || stanceAfter === "charging") return landAtEnd(player);
  }
  // 回球、终局撞上 react 等到帧 4（C2）；终局在空中时起点对不上，由播放器挂起到落地以后（B7、G1）；别的事件当场切
  return cut(player, clip, ruleEventIdOf(event), { atInterrupt: event.type === "ball_returned" || event.type === "ended" });
}

/**
 * 局结束时还在空中（F0 修订 G2，前端兜底）：ended 之后等了 waitedMs，一次起跳的时长（JUMP_MS）到了还在空中（正在飞向 AIR 或停在 AIR，
 * 也就是 landed 没来），就直接落到 END 的稳定姿势、发 fallback{no_path}（END 画不出来照 G3 退到 WAIT）。landed 来了（在落地、已落地）、本来就不在空中，都原样交回。
 * 组件在结束以后的每一帧调用；减少动态效果时结尾不播动作，当场调用、不等。
 */
export function settleAfterEnded(player: MotionPlayer, waitedMs: number): { player: MotionPlayer; events: MotionEvent[] } {
  if (waitedMs < JUMP_MS || headingTo(player) !== "AIR") return { player, events: [] };
  return player.motion.stable.has("END") ? landAtEnd(player) : holdOrRetreat(player, "END");
}

/** 正在去的状态：过渡段在播就是它的终点，否则是站着的状态 */
function headingTo(player: MotionPlayer): string {
  const clip = player.clip;
  return clip && !clip.loop && clip.from_state !== clip.to_state ? clip.to_state : player.state;
}

/** 着地、自己有稳定姿势的状态（G3「原地停」）；AIR 是半空，不算 */
const GROUNDED: readonly string[] = ["HOLD", "CHARGE", "WAIT"];
/** 退的顺序（G3：篮球先找 WAIT，再找 HOLD） */
const RETREAT: readonly string[] = ["WAIT", "HOLD"];

/** 没有不经规则段的路：直接站到 END 的稳定姿势（end 末帧），挂起的段一并清掉，发 fallback{no_path}。调用方先确认 END 画得出来。 */
function landAtEnd(player: MotionPlayer): { player: MotionPlayer; events: MotionEvent[] } {
  const landed = settle({ ...player, target: "END", pending: null }).player;
  return { player: landed, events: [{ type: "fallback", reason: "no_path", clip_key: null, state: "END" }, { type: "arrived", state: "END" }] };
}

/**
 * 要落到的 target 画不出来（F0 修订 G3 及 02:34:11 的说明）：不落过去，挂起的段一并清掉，发 fallback{no_target_pose}（带 target）。
 * 站在着地的状态上（不在过渡途中）：不动，正在播的循环（或这个状态的一次性段）接着播，不定格；
 * 过渡途中或在空中：不走过渡，直接站到 WAIT，没有再站到 HOLD，播它的待机循环（发 arrived）；都画不出来就原样（只清挂起）。
 */
function holdOrRetreat(player: MotionPlayer, target: string): { player: MotionPlayer; events: MotionEvent[] } {
  const events: MotionEvent[] = [{ type: "fallback", reason: "no_target_pose", clip_key: null, state: target }];
  const standing = headingTo(player) === player.state && GROUNDED.includes(player.state);
  const to = standing ? null : (RETREAT.find((state) => player.motion.stable.has(state)) ?? null);
  if (to === null) return { player: { ...player, pending: null }, events };
  const stood = standAt(player, to);
  return { player: stood.player, events: [...events, ...stood.events] };
}

/** 球在爪里此刻画在哪（场地坐标）；null＝这一帧不画「球在爪里」，交给规则的持球点 */
export function ballInPaw(player: MotionPlayer, placement: BasketPlacement, looseBall: { x: number; y: number } | null): { x: number; y: number } | null {
  const canvas = player.motion.set.canvas;
  const frame = currentFrame(player);
  if (frame.hold_point) return holdPointOnCourt(placement, frame.hold_point, canvas);
  const clip = player.clip;
  if (!clip) return null;
  const first = clip.frames.findIndex((f) => f.hold_point);
  if (first <= player.frame) return null;
  const target = holdPointOnCourt(placement, clip.frames[first].hold_point!, canvas);
  if (!looseBall) return target;
  const done = (player.frame + player.elapsed / clip.frames[player.frame].duration_ms) / first;
  return { x: looseBall.x + (target.x - looseBall.x) * done, y: looseBall.y + (target.y - looseBall.y) * done };
}
